/**
 * ReplayUI.ts — Interactive playback controls and history panel overlay for Replays.
 */

export interface ReplayUICallbacks {
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onLast: () => void;
  onToggleAutoPlay: () => boolean; // returns true if playing
  onJumpToStep: (stepIndex: number) => void;
  onCopyCode: () => void;
  onBackToMenu: () => void;
  getChatLog?: () => import('../core/replay').ReplayChatMessage[];
}

export interface ReplayLogItem {
  stepIndex: number;
  teamIndex: number;
  expression: string;
  hits: number;
}

export class ReplayUI {
  private container: HTMLDivElement | null = null;
  private turnCounterEl: HTMLSpanElement | null = null;
  private autoPlayBtn: HTMLButtonElement | null = null;
  private historyListEl: HTMLDivElement | null = null;
  private historyPanelEl: HTMLDivElement | null = null;
  private callbacks: ReplayUICallbacks | null = null;

  show(callbacks: ReplayUICallbacks, totalTurns: number, logs: ReplayLogItem[]): void {
    this.destroy();
    this.callbacks = callbacks;

    const container = document.createElement('div');
    container.className = 'hud-container';
    container.id = 'replay-ui';

    container.innerHTML = `
      <div class="hud-topbar">
        <div class="hud-score">
          <div class="hud-team hud-team--blue">
            <span class="hud-team-dot"></span>
            <span>YOU</span>
          </div>
          <span class="hud-vs">vs</span>
          <div class="hud-team hud-team--red">
            <span class="hud-team-dot"></span>
            <span>BOT</span>
          </div>
        </div>
        <div class="hud-turn-indicator hud-turn-indicator--player" id="replay-counter">
          Turn 0 / ${totalTurns}
        </div>
      </div>

      <div class="hud-history hud-history--hidden" id="replay-history">
        <div class="hud-history-header">
          <span class="hud-history-title">Turn History</span>
          <button class="hud-history-close-btn" id="replay-history-close">✕</button>
        </div>
        <div class="hud-history-list" id="replay-history-list"></div>
      </div>

      <div class="hud-bottombar">
        <div class="hud-input-row" style="justify-content: center; gap: 8px;">
          <button class="hud-action-btn" id="r-btn-first" title="First Turn">|◀</button>
          <button class="hud-action-btn" id="r-btn-prev" title="Previous Turn">◀</button>
          <button class="hud-fire-btn" id="r-btn-play" style="min-width: 80px;">▶ Play</button>
          <button class="hud-action-btn" id="r-btn-next" title="Next Turn">▶</button>
          <button class="hud-action-btn" id="r-btn-last" title="Last Turn">▶|</button>
        </div>
      </div>

      <div class="hud-actions">
        <button class="hud-action-btn" id="r-btn-menu">← Menu</button>
        <button class="hud-action-btn" id="r-btn-copy">Copy Code</button>
        <button class="hud-action-btn" id="r-btn-history">History</button>
      </div>
    `;

    document.body.appendChild(container);
    this.container = container;

    this.turnCounterEl = container.querySelector('#replay-counter');
    this.autoPlayBtn = container.querySelector('#r-btn-play');
    this.historyListEl = container.querySelector('#replay-history-list');
    this.historyPanelEl = container.querySelector('#replay-history');

    // Populate history log
    if (this.historyListEl) {
      let html = '';
      const chatLog = this.callbacks?.getChatLog ? this.callbacks.getChatLog() : [];
      let chatIdx = 0;

      for (let i = 0; i <= logs.length; i++) {
        // Add chat messages that happened before this turn
        while (chatIdx < chatLog.length && chatLog[chatIdx].turnIndex === i) {
          const chat = chatLog[chatIdx];
          html += `
            <div class="hud-chat-entry ${chat.isPlayer ? 'hud-chat-entry--self' : ''}" data-step="${i}" style="margin-top: 4px; padding: 4px 8px; border-radius: 4px; background: rgba(0,0,0,0.05); font-family: var(--font-ui); font-size: 11px;">
              <strong>${chat.senderName}:</strong> ${chat.text}
            </div>
          `;
          chatIdx++;
        }

        // Add the turn
        if (i < logs.length) {
          const log = logs[i];
          const isTeam1 = log.teamIndex === 0;
          const teamClass = isTeam1 ? 'hud-history-entry--team0' : 'hud-history-entry--team1';
          const label = isTeam1 ? 'YOU' : 'BOT';
          html += `
            <div class="hud-history-entry ${teamClass}" data-step="${i + 1}" style="cursor: pointer; transition: background 150ms var(--ease), border-color 150ms var(--ease);">
              <span class="hud-history-who">${label}</span>
              <span class="hud-history-fn" title="f(x) = ${log.expression}">f(x) = ${log.expression}</span>
              ${log.hits > 0 ? `<span class="hud-history-result hud-history-result--hit">HIT (${log.hits})</span>` : ''}
            </div>
          `;
        }
      }
      this.historyListEl.innerHTML = html;

      const items = this.historyListEl.querySelectorAll('.hud-history-entry');
      items.forEach(item => {
        item.addEventListener('click', () => {
          const step = parseInt((item as HTMLElement).dataset.step || '0', 10);
          this.callbacks?.onJumpToStep(step);
        });
      });
    }

    // Attach button listeners
    container.querySelector('#r-btn-first')?.addEventListener('click', () => this.callbacks?.onFirst());
    container.querySelector('#r-btn-prev')?.addEventListener('click', () => this.callbacks?.onPrev());
    container.querySelector('#r-btn-next')?.addEventListener('click', () => this.callbacks?.onNext());
    container.querySelector('#r-btn-last')?.addEventListener('click', () => this.callbacks?.onLast());
    container.querySelector('#r-btn-menu')?.addEventListener('click', () => this.callbacks?.onBackToMenu());
    container.querySelector('#r-btn-copy')?.addEventListener('click', () => this.callbacks?.onCopyCode());

    this.autoPlayBtn?.addEventListener('click', () => {
      const playing = this.callbacks?.onToggleAutoPlay();
      if (this.autoPlayBtn) {
        this.autoPlayBtn.textContent = playing ? '⏸ Pause' : '▶ Play';
      }
    });

    const historyBtn = container.querySelector('#r-btn-history');
    const closeHistoryBtn = container.querySelector('#replay-history-close');

    historyBtn?.addEventListener('click', () => {
      this.historyPanelEl?.classList.toggle('hud-history--hidden');
    });

    closeHistoryBtn?.addEventListener('click', () => {
      this.historyPanelEl?.classList.add('hud-history--hidden');
    });
  }

  updateProgress(currentStep: number, totalTurns: number, isPlaying: boolean): void {
    if (this.turnCounterEl) {
      this.turnCounterEl.textContent = `Turn ${currentStep} / ${totalTurns}`;
    }
    if (this.autoPlayBtn) {
      this.autoPlayBtn.textContent = isPlaying ? '⏸ Pause' : '▶ Play';
    }

    if (this.historyListEl) {
      const items = this.historyListEl.querySelectorAll('.hud-history-entry');
      items.forEach((item, i) => {
        if (i + 1 === currentStep) {
          (item as HTMLElement).style.background = 'var(--bg-raised)';
          (item as HTMLElement).style.borderColor = 'var(--accent)';
        } else {
          (item as HTMLElement).style.background = 'var(--bg-surface)';
          (item as HTMLElement).style.borderColor = 'var(--border)';
        }
      });
    }
  }

  showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  destroy(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}
